import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { PROVIDER_SERVICE_URLS } from '~/Constants/Urls.constants';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { routeReplacer } from '~/Helpers/String.helper';
import { useProvider } from '~/Hooks';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { IPayouts, IInvoice } from '~/Models/DTO/Payouts.model';
import { TIME_VALUES } from '~/Constants/Date.constants';
import { useRequestMerchants } from '~/requests/providerService/useRequestMerchants';
import { useRequestLocationList } from '~/requests';
import { IMerchant } from '~/Models/DTO/Merchant.models';
export interface IGetPayoutsListProp {
  page?: number;
  pageSize?: number;
  start?: Date;
  end?: Date;
}

const filterCancelled = (list: IPayouts[] | undefined) => {
  if (list === undefined) {
    return;
  }
  return list.filter((item: IPayouts) => item.status !== 'cancelled');
};

const changeToDateObject = (list: IPayouts[]): IPayouts[] =>
  list.map((item: IPayouts) => {
    const [year, month, day] = `${item.service_period}`.split('-');
    // this is because when creating a new date, the month
    // starts from 0(January), day starts from 1
    const newMonth = +month - 1;
    const newDate = new Date(parseInt(year), newMonth, parseInt(day));
    return { ...item, service_period: newDate };
  });

const sortListByDate = (list: IPayouts[] | undefined) => {
  if (list === undefined) {
    return;
  }
  return list.sort((prev, current) => Number(current.service_period) - Number(prev.service_period));
};

const filterByDate = (list?: IPayouts[], startDate?: Date, endDate?: Date) => {
  if (!list?.length || !startDate || !endDate) {
    return;
  }

  const filteredList = list.filter(
    (item) => item.service_period >= startDate && item.service_period <= endDate,
  );
  return filteredList;
};

const addPagination = (
  list: IPayouts[] | undefined,
  { page = 1, pageSize = 10 }: IGetPayoutsListProp,
) => {
  if (list === undefined) {
    return;
  }
  const pageStart = (page - 1) * pageSize;
  const pageEnd = (page - 1) * pageSize + pageSize;

  return list?.filter((item, index) => index >= pageStart && index <= pageEnd);
};

export const useRequestPayoutsList = ({
  page = 0,
  pageSize = 0,
  start,
  end,
}: IGetPayoutsListProp) => {
  const { currentProvider } = useProvider();
  const { data: locationList } = useRequestLocationList({});
  const { data: merchantList }: { data: IMerchant[] } = useRequestMerchants();
  const result = useQuery<IPayouts[], IErrorResponse>(
    [currentProvider?.uuid, merchantList?.length, locationList?.length],
    () => {
      if (!currentProvider?.uuid) {
        return [] as IPayouts[];
      }
      if (!locationList?.length || !merchantList?.length) {
        return [] as IPayouts[];
      }

      const venueIds = locationList
        ?.filter((location) => !!location?.venueAddressId)
        ?.map((location) => location?.venueAddressId?.toString());

      const merchantIds = merchantList
        ?.filter((merchant) => merchant.locationIds?.find((id) => venueIds?.includes(id)))
        ?.map((merchant) => merchant.uuid);

      const urls =
        merchantIds?.length > 0 &&
        merchantIds.map((merchantId) =>
          routeReplacer(PROVIDER_SERVICE_URLS.GET_INVOICES_BY_MERCHANT, {
            providerId: currentProvider?.uuid,
            merchantId,
          }),
        );

      if (!urls) {
        return [] as IPayouts[];
      }
      const callArray = urls?.map((url = '') =>
        requestReactQuery<IPayouts[]>({
          url,
          config: { method: 'GET' },
        }),
      );
      // eslint-disable-next-line promise/prefer-await-to-then
      return Promise.all(callArray).then((values) => values.flat());
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: false,
      refetchInterval: TIME_VALUES.HOUR,
      initialData: [],
    },
  ) as any;

  const noCancelledList = filterCancelled(result.data);
  let modifiedToDate, sortedList, paginatedList: any;
  if (noCancelledList !== undefined) {
    modifiedToDate = changeToDateObject(noCancelledList || []);
    sortedList = sortListByDate(modifiedToDate);
    if (!start && !end) {
      paginatedList = addPagination(sortedList, { page, pageSize });
    }
    if (!!start && !!end) {
      const filteredListByDate = filterByDate(sortedList, start, end);
      paginatedList = addPagination(filteredListByDate, { page, pageSize });
    }
  }
  result.data = useMemo(() => paginatedList, [paginatedList]);
  return result;
};

export const useRequestPayoutsInvoice = (invoiceId: number) => {
  const { currentProvider } = useProvider();

  const { isLoading, isFetching, error, data, refetch } = useQuery<IInvoice, IErrorResponse>(
    [REQUEST_ACTIONS.GET_INVOICE_DOWNLOAD_URL, invoiceId],
    () =>
      requestReactQuery<IInvoice>({
        url: routeReplacer(PROVIDER_SERVICE_URLS.GET_INVOICE_DOWNLOAD_URL, {
          providerId: currentProvider?.uuid || 0,
          invoiceId,
        }),
        config: { method: 'GET' },
      }),
    {
      enabled: false,
    },
  );

  return {
    downloadInvoicesLoading: isLoading,
    downloadInvoicesFetching: isFetching,
    downloadInvoicesError: error,
    downloadInvoicesData: data,
    refetchDownloadInvoices: refetch,
  };
};
